/**
 * Sprint-104d-full — Map provider façade.
 *
 * Re-exports react-leaflet primitives behind a narrow surface so a future
 * Phase-C / production swap to Google Maps (or Mapbox, MapLibre, …) is a
 * single-file change. Consumers MUST import from this module — not from
 * "react-leaflet" directly — for that swap to remain a one-file lift.
 *
 * The named exports mirror the react-leaflet ones we use today. If we
 * ever migrate to Google Maps, we'd implement equivalent thin wrappers
 * with the same prop shape; the call-sites would not need to change.
 *
 * Hard rule from the spec: Leaflet only. Google Maps appears only as an
 * external deep-link, never as an embedded map.
 */
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export const MAP_PROVIDER = "leaflet";

// Default tile layer — Carto's light_all is clean, free, and copyright-safe.
export const DEFAULT_TILE_URL =
  "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
export const DEFAULT_TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

/** Build a Leaflet div-icon dot in the requested color. */
export function createDotIcon(color, size = 18) {
  return L.divIcon({
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `
      <div style="
        width:${size}px;height:${size}px;
        background:${color};border:3px solid white;border-radius:50%;
        box-shadow:0 2px 8px rgba(0,0,0,0.35);
      "></div>
    `,
  });
}

// Re-export react-leaflet primitives. Consumers should always import
// from this module so a provider swap is a one-file change.
export { MapContainer, TileLayer, Marker, Polyline, Popup, useMap, L };
